import {
  ContactsTable,
  HireTable,
  InterviewTable,
  JobsTable,
  OfferTable,
  RejectionTable,
  PipelineTable,
  LiveJobPipelineTable,
  PerformanceTable,
  RequisitionTable,
  SurveyTable,
  RaceTable,
  VeteranTable,
  DisabilityTable,
} from '@/pages/reports/ats-reports/table';

import { Box, Flex, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  customReportActions,
  getCustomReportData,
} from '@/store/custom-report';
import {
  staticReportActions,
  getStaticReportData,
} from '@/store/static-report';
import { Table as FlowCandidateTouchPoint } from '@/pages/reports/non-ats-reports/flow-health/candidate-touchpoint/component/table';
import { Table as FlowUserActivity } from '@/pages/reports/non-ats-reports/flow-health/user-activity/component/table';
import { useState } from 'react';
import { useKFlags } from '@/hooks/use-k-flags';
import { CardTileHeading } from '../CardTileHeading';
import { reportTableConfig } from './reportTableConfig';

const tableCompMap = {
  pipeline_table: PipelineTable,
  live_job_pipeline_table: LiveJobPipelineTable,
  interviews_table: InterviewTable,
  offers_table: OfferTable,
  hires_table: HireTable,
  rejections_table: RejectionTable,
  contacts_table: ContactsTable,
  jobs_table: JobsTable,
  eeoc_race_table: RaceTable,
  eeoc_veteran_table: VeteranTable,
  eeoc_disability_table: DisabilityTable,
  flow_health: FlowCandidateTouchPoint,
  flow_user_activity: FlowUserActivity,
  recruiter_performance_table: PerformanceTable,
  requisitions_table: RequisitionTable,
  survey_table: SurveyTable,
};

const UnknownReportTable = () => <Box>Unknown report</Box>;

const tableColStyles = {
  title: 'Select default columns',
  triggerContainer: {
    position: 'unset',
  },
  trigger: {
    width: '32px',
    height: '32px',
    top: '9px',
    right: '50px',
    position: 'fixed',
    borderRadius: '6px',
    zIndex: '7',
    bgColor: 'primary-100',
    border: '1px solid',
    borderColor: 'secondary-100',
  },
  triggerIcon: {
    width: '16px',
    height: '16px',
    color: 'primary-600',
  },
  popoverContent: {
    maxH: '400px',
  },
};

export const ReportTable = ({
  data,
  tile,
  isFlow = false,
  isReadOnly = false,
  isPrintView = false,
  isAddBlock = false,
  query = undefined,
  isFullScreen = false,
  uniqueTileId = undefined,
  onDrillDownOpen = (_data) => {},
  customStageMapping = {},
  isStaticReport = false,
  onSortChange = (_sortState) => {},
  onTileMetaChange = undefined,
  isEditMode = false,
  unsupportedFilterLabels,
  currentTableFilters = null,
  onTableFilterChange = undefined,
  currentTitle = undefined,
}) => {
  const { subject_type, metadata = {}, id } = tile || {};
  const { tileKey } = metadata;
  const dispatch = useDispatch();
  const { feReportsV3 } = useKFlags();
  const { applyTableGroup } = isStaticReport
    ? staticReportActions
    : customReportActions;
  const tableId = tileKey || id;

  const [tableColData, setTableColData] = useState(metadata.tableColData);

  const reduxData = useSelector((state) =>
    isStaticReport
      ? getStaticReportData(state, tableId)
      : getCustomReportData(state, tableId)
  );

  const { tableGroups, filterParams } = reduxData;

  const {
    metrics: readOnlyMetrics,
    group_by_1: readOnlyG1,
    group_by_2: readOnlyG2,
  } = query || {};
  let effectiveGroupBy1, effectiveGroupBy2, effectiveMetrics;

  if (feReportsV3) {
    // New state-based approach - use filters from props (managed by TileContainer) or fallback to metadata
    const effectiveFilters = currentTableFilters || {
      metrics: metadata?.filters?.metrics || null,
      group_by_1: metadata?.filters?.group_by_1 || null,
      group_by_2: metadata?.filters?.group_by_2 || null,
    };

    effectiveGroupBy1 = effectiveFilters.group_by_1;
    effectiveGroupBy2 = effectiveFilters.group_by_2;
    effectiveMetrics = effectiveFilters.metrics;
  } else {
    // Legacy Redux-based approach

    const { metrics, group_by_1, group_by_2 } =
      tableGroups[uniqueTileId ?? tileKey] || {};

    effectiveGroupBy1 = group_by_1;
    effectiveGroupBy2 = group_by_2;
    effectiveMetrics = metrics;
  }

  //this is used for table group by filters
  const customBreakdownChange = (key, option) => {
    if (feReportsV3) {
      // New state-based approach - delegate to parent TileContainer
      onTableFilterChange?.(key, option);
    }
    // Always mirror to Redux so downstream consumers (PDF download payload,
    // schedule email, etc.) that read from staticReports/customReports
    // tableGroups see the user's latest selection. Without this, feReportsV3
    // keeps the breakdown only in component-local state and downloads ship
    // with default group_by_1/group_by_2.
    dispatch(applyTableGroup({ tableId, groupKey: key, value: option }));
  };

  // Handle table column data changes
  const handleTableColDataChange = (tableColData) => {
    onTileMetaChange?.(id, {
      tableColData,
    });
    setTableColData(tableColData);
  };

  const { title, metricDefinition, basedOn, category, tag } =
    reportTableConfig[tileKey] || {};

  const renderTitle = () => {
    return (
      <Flex
        alignItems="flex-start"
        gap="12px"
        flex={1}
        minWidth={isPrintView ? '200px' : undefined}
      >
        <CardTileHeading
          tileId={tile?.id}
          label={tile?.metadata?.title ?? title}
          metricDefinition={isFullScreen ? undefined : metricDefinition}
          basedOn={basedOn}
          category={category}
          isStaticReport={isStaticReport}
          containerStyles={{
            flex: 1,
            maxWidth: isPrintView ? '100%' : '300px',
          }}
          onTileMetaChange={onTileMetaChange}
          currentTitle={currentTitle}
          subjectType={subject_type}
          unsupportedFilterLabels={unsupportedFilterLabels}
          isPrintView={isPrintView}
        />
        {tag && (
          <Text
            size="sm-medium"
            bg="ats-tertiary"
            borderRadius="sm"
            w="fit-content"
            h="fit-content"
            p="4px 8px"
          >
            {tag}
          </Text>
        )}
      </Flex>
    );
  };

  const TableComp =
    tableId && tableCompMap[tableId]
      ? tableCompMap[tableId]
      : UnknownReportTable;

  return (
    <Box h={'100%'} overflow={'hidden'}>
      <TableComp
        key={`${tableId}-${isEditMode}`}
        tile={tile}
        customData={data}
        isReadOnly={isReadOnly}
        isPrintView={isPrintView}
        isStaticReport={isStaticReport}
        groupBy1={readOnlyG1 ? { id: readOnlyG1 } : effectiveGroupBy1}
        groupBy2={readOnlyG2 ? { id: readOnlyG2 } : effectiveGroupBy2}
        customBreakdownChange={customBreakdownChange}
        onDrillDownOpen={onDrillDownOpen}
        renderTitle={renderTitle}
        customStageMapping={customStageMapping}
        isFullScreen={isFullScreen}
        onSortChange={onSortChange}
        isEditMode={Boolean(onTileMetaChange)}
        tableColStyles={tableColStyles}
        onTableColDataChange={
          onTileMetaChange ? handleTableColDataChange : undefined
        }
        initialTableColData={tableColData}
        feReportsV3={feReportsV3}
        {...(subject_type === 'pipeline' || id === 'pipeline_table'
          ? {
              metric: readOnlyMetrics
                ? readOnlyMetrics || 'passthrough_rate'
                : effectiveMetrics?.id || 'passthrough_rate',
            }
          : subject_type === 'live_job_pipeline' ||
              tileKey === 'live_job_pipeline_table'
            ? {
                metric: 'time_in_stage',
              }
            : {})}
        hideLinks={isAddBlock}
        skipLocalStorage
        {...(isFlow
          ? {
              filters: {
                group_by: readOnlyG1 || effectiveGroupBy1?.id,
                break_down: readOnlyG2 || effectiveGroupBy2?.id,
                metrics: readOnlyMetrics || effectiveMetrics?.id,
                user_activity_break_down: readOnlyG2 || effectiveGroupBy2?.id,
                user_activity_group_by: readOnlyG1 || effectiveGroupBy1?.id,
              },
              filterParams,
            }
          : {})}
      />
    </Box>
  );
};
